import { CheckIcon, XIcon } from '@phosphor-icons/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { I18nNamespace } from '@/lib/i18n-namespaces';

interface SuggestionPanelProps {
    isLoading: boolean;
    text: string;
    onAccept: () => void;
    onDecline: () => void;
}

export function SuggestionPanel({
    isLoading,
    text,
    onAccept,
    onDecline,
}: SuggestionPanelProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const prefersReducedMotion = useReducedMotion();

    const motionProps = prefersReducedMotion
        ? {
              initial: { opacity: 0 },
              animate: { opacity: 1 },
              exit: { opacity: 0 },
              transition: { duration: 0 },
          }
        : {
              initial: { opacity: 0 },
              animate: { opacity: 1 },
              exit: { opacity: 0 },
              transition: { duration: 0.15, ease: 'easeOut' as const },
          };

    const layoutTransition = prefersReducedMotion
        ? { duration: 0 }
        : { duration: 0.2, ease: [0.16, 1, 0.3, 1] as const };

    return (
        <motion.div
            layout
            transition={{ layout: layoutTransition }}
            className="overflow-hidden rounded-lg bg-grey-550"
        >
            <AnimatePresence mode="popLayout" initial={false}>
                {isLoading && (
                    <motion.div
                        key="suggestion-loading"
                        {...motionProps}
                        className="p-3"
                        aria-live="polite"
                        aria-busy="true"
                    >
                        <div className="flex items-center justify-between">
                            <div className="h-3 w-24 rounded skeleton-item-bright" />
                            <div className="flex items-center gap-1">
                                <div className="size-5 mr-3 rounded skeleton-item-bright" />
                                <div className="size-5 rounded skeleton-item-bright" />
                            </div>
                        </div>
                        <div className="mt-3 space-y-2">
                            <div className="h-3 w-full rounded skeleton-item-bright" />
                            <div className="h-3 w-2/3 rounded skeleton-item-bright" />
                        </div>
                    </motion.div>
                )}
                {!isLoading && text && (
                    <motion.div
                        key="suggestion-shown"
                        {...motionProps}
                        className="p-3 pt-2"
                        aria-live="polite"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-grey-200 font-medium">
                                {t('promptInput.suggestion.title')}
                            </span>
                            <div className="flex items-center gap-1">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    shape="square"
                                    size="small"
                                    className="size-7 min-w-7 p-0 text-grey-200 hover:text-white-100"
                                    aria-label={t(
                                        'promptInput.suggestion.decline',
                                    )}
                                    leadingIcon={<XIcon className="size-4" />}
                                    onClick={onDecline}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    shape="square"
                                    size="small"
                                    className="size-7 min-w-7 p-0 text-grey-200 hover:text-white-100"
                                    aria-label={t(
                                        'promptInput.suggestion.accept',
                                    )}
                                    leadingIcon={
                                        <CheckIcon className="size-4" />
                                    }
                                    onClick={onAccept}
                                />
                            </div>
                        </div>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-white-100">
                            {text}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
